// PersonAvatar — a name + profile-picture avatar for conversations, wrapping the
// shared Radix Avatar primitive (image with graceful initials fallback). One place so
// the inbox, message bubbles, thread header and details panel all render people the
// same way. Size is in px; the ring is off by default (bubbles sit on tinted grounds).
import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/features/shared/components/ui/avatar';
import { cn } from '@/lib/utils';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const chars = parts.length > 1 ? parts[0][0] + parts[1][0] : name.replace(/[^A-Za-z0-9]/g, '').slice(0, 2);
  return (chars || '·').toUpperCase();
}

interface Props {
  name: string;
  avatarUrl?: string;
  size?: number; // px
  className?: string;
  ring?: boolean; // white ring (useful when overlapping/stacked)
}

export const PersonAvatar: React.FC<Props> = ({ name, avatarUrl, size = 40, className, ring }) => (
  <Avatar
    style={{ height: size, width: size }}
    className={cn('flex-none', ring ? 'border-2 border-card' : 'border-0', className)}
  >
    {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
    <AvatarFallback
      className="bg-primary-100 font-semibold text-primary-700"
      style={{ fontSize: Math.max(10, Math.round(size * 0.36)) }}
    >
      {initialsOf(name)}
    </AvatarFallback>
  </Avatar>
);
